import React, { useMemo } from 'react';
import { Spinner } from 'components/UI';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';

import type { MonthlyBreakdownReport, MonthKey } from 'model/BookingsMonthlyBreakdownReport';
import styles from './MonthlyBreakdownsTable.module.scss';

interface MonthlyBreakdownsTableProps {
    loading: boolean;
    report: MonthlyBreakdownReport;
}

const MONTH_KEYS: MonthKey[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const MONTH_LABELS: Record<MonthKey, string> = {
    jan: 'January',
    feb: 'February',
    mar: 'March',
    apr: 'April',
    may: 'May',
    jun: 'June',
    jul: 'July',
    aug: 'August',
    sep: 'September',
    oct: 'October',
    nov: 'November',
    dec: 'December'
};

const money = (n: number) =>
    n.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

export default function MonthlyBreakdownsTable({ loading, report }: MonthlyBreakdownsTableProps) {
    const rows = useMemo(() => {
        const getMonthValues = (values: Record<MonthKey | 'total', number>): number[] =>
            MONTH_KEYS.map(k => values?.[k] ?? 0);

        const getCountValues = (row: MonthlyBreakdownReport['completed']): number[] =>
            MONTH_KEYS.map(k => row?.values?.[k]?.value ?? 0);

        return [
            { metric: 'Total Bookings', months: getMonthValues(report.totalBookings) },
            { metric: 'Total Clients', months: getMonthValues(report.totalClients) },
            { metric: 'Total Pets', months: getMonthValues(report.totalPets) },

            { metric: 'Total Services', months: getMonthValues(report.totalServices) },
            { metric: 'Total Add-Ons', months: getMonthValues(report.totalAddons) },
            { metric: 'Total Products', months: getMonthValues(report.totalProducts) },
            { metric: 'Total Custom Items', months: getMonthValues(report.totalCustomItems) },

            { metric: 'Total Completed', months: getCountValues(report.completed) },
            { metric: 'Total Canceled', months: getCountValues(report.canceled) },
            { metric: 'Total No Shows', months: getCountValues(report.noShow) },

            // Net Sales como currency
            { metric: 'Net Sales', months: getMonthValues(report.netSales), isCurrency: true }
        ];
    }, [report]);

    return (
        <div className={styles.root}>
            <Table
                fullHeight
                firstColumnFixed
            >
                <TableHeader>
                    <TableHeaderCell style={{ minWidth: 180 }}></TableHeaderCell>

                    {MONTH_KEYS.map(k => (
                        <TableHeaderCell
                            key={k}
                            style={{ minWidth: 100 }}
                        >
                            {MONTH_LABELS[k]}
                        </TableHeaderCell>
                    ))}
                </TableHeader>

                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell
                                colSpan={1 + MONTH_KEYS.length}
                                className={styles.loadingCell}
                            >
                                <Spinner className={styles.spinner} />
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell className={styles['cell-emphasis']}>{row.metric}</TableCell>
                                {row.months.map((val, i) => (
                                    <TableCell key={i}>{row.isCurrency ? money(val) : val}</TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
